import { useEffect, useState } from 'react';
import { api, slotLabel, type Colleagues } from '../lib/api';
import { addDays, dayLabel, isoToday } from '../lib/dates';

/**
 * Who else is working this week, at the hospitals this physician is at (D56).
 *
 * Published weeks only — the same rule as "My shifts". A physician must never learn from this
 * page that a week exists before the office has published it.
 *
 * Contact details are withheld until OSMD turns them on. When they are off the page says so
 * plainly rather than quietly showing names and letting somebody wonder where the numbers are.
 */
export function MyColleagues({ abbreviation }: { abbreviation: string }) {
  // Null until the first answer comes back: the server picks a week the physician is actually
  // working, and the arrows move from there.
  const [week, setWeek] = useState<string | null>(null);
  const [data, setData] = useState<Colleagues | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setData(null);
    setError(null);
    api
      .myColleagues(abbreviation, week ?? '')
      .then((d) => { if (ignore) return; if (week && d.week !== week) return; setData(d); if (!week) setWeek(d.week); })
      .catch((e: Error) => { if (!ignore) setError(e.message); });
    return () => { ignore = true; };
  }, [abbreviation, week]);

  const step = (by: number) => setWeek((w) => addDays(w ?? isoToday(), by * 7));

  return (
    <section className="colleagues">
      <div className="weekpick">
        <button type="button" onClick={() => step(-1)} aria-label="The week before">‹</button>
        <h1>{week ? <>{dayLabel(week).replace(/^\w+, /, '')} – {dayLabel(addDays(week, 6)).replace(/^\w+, /, '')}</> : 'This week'}</h1>
        <button type="button" onClick={() => step(1)} aria-label="The week after">›</button>
      </div>

      {error && <p className="error">Couldn't load that week ({error}).</p>}
      {!data && !error && <p className="muted">Looking…</p>}

      {data && (
        data.sites.length === 0 ? (
          <p className="muted">
            You are not on a published schedule that week, so there is nobody to show. A week only appears here once the office has
            published it — if you know you are working, it is probably not published yet.
          </p>
        ) : (
          <>
            {!data.contactsShown && (
              <p className="notice small">
                Email addresses and phone numbers are not shown yet — Open Source MD is asking physicians first.
              </p>
            )}
            {data.sites.map((s) => (
              <section key={s.siteId} className="colleaguesite">
                <h2>{s.siteName}</h2>
                {s.colleagues.length === 0 ? (
                  <p className="muted small">Nobody else is on the published schedule there that week.</p>
                ) : (
                  <ul className="colleaguelist">
                    {s.colleagues.map((c) => (
                      <li key={c.abbreviation}>
                        <div className="who"><strong>{c.firstName} {c.lastName}</strong></div>
                        <div className="where">
                          <span>
                            {c.positionSlots.map(slotLabel).join(', ')}
                            <span className="muted"> · {dayLabel(c.from).replace(/^\w+, /, '').replace(/, \d{4}$/, '')}–{dayLabel(c.to).replace(/^\w+, /, '').replace(/, \d{4}$/, '')}</span>
                          </span>
                        </div>
                        {data.contactsShown && (
                          <div className="reach">
                            {c.phone ? <a href={`tel:${c.phone.replace(/[^\d+]/g, '')}`}>{c.phone}</a> : <span className="muted">no number on file</span>}
                            {c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : <span className="muted">no email on file</span>}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </>
        )
      )}
    </section>
  );
}
